import React, { useEffect } from 'react';
import { useThrottleEffect } from 'ahooks';
import SubscribeModal from './SubscribeModal';
import styled from './index.module.scss';
import { ShopIcon, Referral, WayIcon1, WayIcon2, WayIcon3, WayIcon4, WayIcon5, WayIcon6 } from '@/components/UI/svg/Membership';
import { formOfFetch, setLocaleQuery, getHostApi } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import { EFPureSelectIcon } from '@/components/UI/svg';
import { EFArrow } from '@/components/UI/svg/Search';
import Button from '@/components/UI/Button';
import EfModal from '@/components/UI/Modal';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface props {
    isLogin: boolean;
    rewardEarnRule: any[];
    refershData: () => void;
}

const WaysToEarnPoints: React.FC<props> = ({ isLogin, rewardEarnRule, refershData }) => {
    const { Link, useRouter, useLang, useState, gtm, subEmailCount } = useGlobal();
    const { t } = useLang('membership.waystoearnpoints');
    const { t: _t } = useLang('ecocredits');
    const router = useRouter();
    const { locale, route } = router;
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

    const birthdayRule = rewardEarnRule.find((rule) => rule.source2 === 'ACCOUNT_INFO_BIRTHDAY');
    const subscribeRule = rewardEarnRule.find((rule) => rule.source2 === 'EDM');

    const firstData = [
        {
            key: 'shop',
            title: t('icon1_title'),
            desc: t('icon1_desc'),
            icon: <ShopIcon />,
            btnText: t('icon1_btn'),
            link: locale === 'es' ? 'https://es.ecoflow.com/collections/promotional-items' : `https://${locale}.ecoflow.com`,
            onclick: () =>
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'buy',
                    product_name: 'None',
                    position: 'EcoCredit Page',
                }),
        },
        {
            key: 'referral',
            title: t('icon2_title'),
            desc: t('icon2_desc'),
            icon: <Referral />,
            btnText: t('icon2_btn'),
            link: `/${locale}/referral`,
            onclick: () =>
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'activity_button',
                    page_name: 'membership',
                    list_name: 'EcoCredits Benefits',
                    button_name: t('icon2_btn'),
                    function: 'Referral',
                }),
        },
    ];

    const secondData = [
        {
            key: 'sign_up',
            title: t('icon3_title'),
            points: '500 ',
            icon: <WayIcon1 />,
            isFinish: isLogin,
        },
        {
            key: 'subscribe',
            title: t('icon4_title'),
            points: '500 ',
            icon: <WayIcon2 />,
            btnText: t('icon4_btn'),
            onclick: () => setSubscribeModalOpen(true),
            isFinish: !subscribeRule || subscribeRule?.isFinish === 1,
        },
        {
            key: 'warranty',
            title: t('icon5_title'),
            points: '1000 ',
            icon: <WayIcon3 />,
            isFinish: false,
            btnText: t('icon5_btn'),
            onclick: () => router.push(`/${locale}/support/warranty`),
        },
        {
            key: 'birthday',
            title: t('icon6_title'),
            points: '200 ',
            icon: <WayIcon4 />,
            isFinish: !birthdayRule || birthdayRule?.isFinish === 1,
            btnText: t('icon6_btn'),
            onclick: () => router.push(`/${locale}/my`),
        },
        {
            key: 'profile',
            title: t('icon7_title'),
            points: '800 ',
            icon: <WayIcon5 />,
            isFinish: rewardEarnRule?.filter((rule) => rule.source === 'ACCOUNT_INFO')?.every((rule) => rule?.isFinish === 1),
            btnText: t('icon7_btn'),
            onclick: () => router.push(`/${locale}/my`),
        },
        {
            title: t('icon8_title'),
            points: 'Double ',
            subtitle: locale === 'fr' || locale === 'es' ? t('double_ecocredits') : '',
            icon: <WayIcon6 />,
            isFinish: false,
        },
    ];

    const pollingData = () => {
        const delayTimer = [3, 5];
        delayTimer.forEach((timer) => {
            setTimeout(() => {
                refershData && refershData();
            }, timer * 1000);
        });
    };

    useThrottleEffect(() => {
        if (subEmailCount === 0) return;
        pollingData();
    }, [subEmailCount]);

    return (
        <>
            <div className={styled.ways_to_earn_points} id="How_to_Earn">
                <h5 className="title">{t('title')}</h5>
                <div className="permanent">
                    {firstData.map((item) => (
                        <div key={item.key} className="permanent_item">
                            <p className="permanent_item_title">{item.title}</p>
                            <p className="permanent_item_desc">{item.desc}</p>
                            <Link href={item.link} target="_blank">
                                <Button className="permanent_item_btn" onClick={item.onclick}>
                                    {item.btnText}
                                </Button>
                            </Link>
                            <span className="icon">{item.icon}</span>
                        </div>
                    ))}
                </div>
                <div className="one_time">
                    {secondData.map((item, index) => (
                        <div key={item.key} className={isLogin || index === secondData.length - 1 ? 'one_time_item' : 'one_time_item unlogin'}>
                            <div className="one_time_item_box">
                                {item.isFinish && isLogin ? (
                                    <div className="one_time_icon finish">
                                        <EFPureSelectIcon />
                                    </div>
                                ) : (
                                    <div className="one_time_icon">{item.icon}</div>
                                )}
                                <p className="one_time_title">{item.title}</p>
                                <p className="one_time_award">
                                    {item.subtitle ? (
                                        item.subtitle
                                    ) : (
                                        <>
                                            <span className="points">{item.points}</span>
                                            {_t('points')}
                                        </>
                                    )}
                                </p>
                                {item.btnText && !item.isFinish && isLogin && (
                                    <Button type="link" className="one_time_btn" onClick={item.onclick}>
                                        {item.btnText} <EFArrow />
                                    </Button>
                                )}
                            </div>
                            <div className="one_time_unlogin">
                                <Button
                                    type="link"
                                    className="one_time_unlogin_btn"
                                    onClick={() => {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'ecocredits_button',
                                            type: 'Register',
                                            list_name: t('title'),
                                            button_name: t('no_login_btn'),
                                        });
                                        signupCookie?.({
                                            sector_name: t('title'),
                                            entry_position: 4,
                                        });
                                        router.push(`/${locale}/register?redirect=${encodeURIComponent(`/${locale}${route}#How_to_Earn`)}`);
                                    }}
                                >
                                    {t('no_login_btn')}
                                </Button>
                                <p className="one_time_unlogin_desc">
                                    {t.rich('no_login_text', {
                                        line: (val: string) => (
                                            <span
                                                className="link"
                                                onClick={() => {
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'ecocredits_button',
                                                        type: 'Login',
                                                        list_name: t('title'),
                                                        button_name: val[0],
                                                    });
                                                    signupCookie?.({
                                                        sector_name: t('title'),
                                                        entry_position: 4,
                                                    });
                                                    router.push(`/${locale}/login?redirect=${encodeURIComponent(`/${locale}${route}#How_to_Earn`)}`);
                                                }}
                                            >
                                                {val}
                                            </span>
                                        ),
                                    })}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <EfModal className={styled.efmodal} visible={profileModalOpen} onCancel={() => setProfileModalOpen(false)} zIndex={1200}>
                <div className="note-content">
                    <h4 className="rule-title">{_t('profile_a')}</h4>
                    <p className="rule-desc">{_t('profile_detail')}</p>
                </div>
            </EfModal>

            <SubscribeModal open={subscribeModalOpen} closeModal={() => setSubscribeModalOpen(false)} onSuccess={() => pollingData()} />
        </>
    );
};

export default WaysToEarnPoints;
